const PROXY_URL = '/apps/wishlist/sync';

/**
 * @typedef {object} WishlistItem
 * @property {string} id
 * @property {string} handle
 * @property {number} addedAt
 */

/**
 * @typedef {object} WishlistList
 * @property {string} id
 * @property {string} name
 * @property {boolean} isDefault
 * @property {string} [shareSlug] - set once the list has a public share link
 * @property {WishlistItem[]} items
 */

/**
 * @typedef {object} WishlistState
 * @property {WishlistList[]} lists
 */

/**
 * @typedef {object} WishlistEventDetail
 * @property {string} action
 * @property {number} [totalCount]
 * @property {string} [listId]
 * @property {string} [productId]
 * @property {string} [fromListId]
 * @property {string} [toListId]
 */

export class WishlistStore {
  static #LOCAL_KEY = 'birch_wishlists';
  /** Tracks which customer the locally-cached lists belong to. */
  static #OWNER_KEY = 'birch_wishlists_owner';
  /** @type {ReturnType<typeof setTimeout> | null} */
  static #syncTimer = null;

  /**
   * @returns {WishlistState}
   */
  static getLists() {
    const raw = localStorage.getItem(this.#LOCAL_KEY);
    if (raw == null) return this.#defaultState();
    try {
      const parsed = JSON.parse(raw);
      return /** @type {WishlistState} */ (parsed ?? this.#defaultState());
    } catch {
      return this.#defaultState();
    }
  }

  /**
   * @param {WishlistState} state
   */
  static #save(state) {
    localStorage.setItem(this.#LOCAL_KEY, JSON.stringify(state));
  }

  static getDefaultList() {
    return this.getLists().lists.find((l) => l.isDefault) ?? this.getLists().lists[0];
  }

  static get totalCount() {
    return this.getLists().lists.reduce((n, l) => n + l.items.length, 0);
  }

  /**
   * @param {string | number} productId
   */
  static hasItem(productId) {
    return this.getLists().lists.some((l) => l.items.some((i) => i.id === String(productId)));
  }

  /**
   * @param {string} listId
   * @param {string | number} productId
   */
  static hasItemInList(listId, productId) {
    const list = this.getLists().lists.find((l) => l.id === listId);
    return list?.items.some((i) => i.id === String(productId)) ?? false;
  }

  /**
   * @param {string} name
   */
  static createList(name) {
    const state = this.getLists();
    const id = `list-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    state.lists.push({ id, name, isDefault: false, items: [] });
    this.#save(state);
    this.#dispatch({ action: 'create', listId: id });
    this.syncUp();
    return id;
  }

  /**
   * @param {string} listId
   * @param {string} name
   */
  static renameList(listId, name) {
    const state = this.getLists();
    const list = state.lists.find((l) => l.id === listId);
    if (!list) return;
    list.name = name;
    this.#save(state);
    this.#dispatch({ action: 'rename', listId });
    this.syncUp();
  }

  /**
   * @param {string} listId
   */
  static deleteList(listId) {
    const state = this.getLists();
    const idx = state.lists.findIndex((l) => l.id === listId);
    if (idx === -1) return;
    const removed = state.lists[idx];
    if (!removed) return;
    const wasDefault = removed.isDefault;
    state.lists.splice(idx, 1);
    if (wasDefault && state.lists.length > 0) {
      const first = state.lists[0];
      if (first) first.isDefault = true;
    }
    this.#save(state);
    this.#dispatch({ action: 'delete', listId });
    this.syncUp();
  }

  /**
   * @param {string} listId
   */
  static clearList(listId) {
    const state = this.getLists();
    const list = state.lists.find((l) => l.id === listId);
    if (!list) return;
    list.items = [];
    this.#save(state);
    this.#dispatch({ action: 'clear', listId });
    this.syncUp();
  }

  /**
   * @param {string} listId
   */
  static setDefault(listId) {
    const state = this.getLists();
    state.lists.forEach((l) => { l.isDefault = l.id === listId; });
    this.#save(state);
    this.#dispatch({ action: 'setDefault', listId });
    this.syncUp();
  }

  /**
   * @param {string} listId
   * @param {string | number} productId
   * @param {string} handle
   */
  static addItem(listId, productId, handle) {
    const state = this.getLists();
    const list = state.lists.find((l) => l.id === listId);
    if (!list) return;
    if (list.items.some((i) => i.id === String(productId))) return;
    list.items.unshift({ id: String(productId), handle, addedAt: Date.now() });
    this.#save(state);
    this.#dispatch({ action: 'add', listId, productId: String(productId) });
    this.syncUp();
  }

  /**
   * @param {string} listId
   * @param {string | number} productId
   */
  static removeItem(listId, productId) {
    const state = this.getLists();
    const list = state.lists.find((l) => l.id === listId);
    if (!list) return;
    list.items = list.items.filter((i) => i.id !== String(productId));
    this.#save(state);
    this.#dispatch({ action: 'remove', listId, productId: String(productId) });
    this.syncUp();
  }

  /**
   * @param {string | number} productId
   * @param {string} fromListId
   * @param {string} toListId
   */
  static moveItem(productId, fromListId, toListId) {
    const state = this.getLists();
    const from = state.lists.find((l) => l.id === fromListId);
    const to = state.lists.find((l) => l.id === toListId);
    if (!from || !to) return;
    const item = from.items.find((i) => i.id === String(productId));
    if (!item) return;
    from.items = from.items.filter((i) => i.id !== String(productId));
    if (!to.items.some((i) => i.id === String(productId))) to.items.unshift(item);
    this.#save(state);
    this.#dispatch({ action: 'move', productId: String(productId), fromListId, toListId });
    this.syncUp();
  }

  /**
   * Mint (or fetch) a public share link for a single list. Opt-in: only called
   * on an explicit user action. The slug↔owner mapping is created server-side
   * in KV; we mirror the returned slug onto the local list so the UI reflects
   * shared state and it syncs across the customer's devices.
   * @param {string} listId
   * @returns {Promise<string | null>} the slug, or null on failure
   */
  static async share(listId) {
    if (!Theme.customerId) return null;
    const state = this.getLists();
    const list = state.lists.find((l) => l.id === listId);
    if (!list) return null;
    if (list.shareSlug) return list.shareSlug;
    try {
      const resp = await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: `gid://shopify/Customer/${Theme.customerId}`,
          namespace: 'custom',
          key: 'wishlists',
          action: 'share',
          listId,
        }),
      });
      if (!resp.ok) return null;
      const body = await resp.json();
      const slug = typeof body?.slug === 'string' ? body.slug : null;
      if (!slug) return null;
      list.shareSlug = slug;
      this.#save(state);
      this.#dispatch({ action: 'share', listId });
      // The public read authorizes against the owner's metafield, so make sure
      // it advertises this slug before we hand the link back to be copied.
      await this.#doSyncUp();
      return slug;
    } catch {
      return null;
    }
  }

  /**
   * Revoke a list's public share link. The KV mapping is deleted server-side
   * and the local slug cleared so the link 404s immediately.
   * @param {string} listId
   * @returns {Promise<boolean>}
   */
  static async unshare(listId) {
    if (!Theme.customerId) return false;
    const state = this.getLists();
    const list = state.lists.find((l) => l.id === listId);
    if (!list) return false;
    // Pass the slug we hold so the worker revokes it directly, not only via its
    // reverse index (which a double-share race can leave pointing elsewhere).
    const slug = list.shareSlug;
    try {
      const resp = await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: `gid://shopify/Customer/${Theme.customerId}`,
          namespace: 'custom',
          key: 'wishlists',
          action: 'unshare',
          listId,
          slug,
        }),
      });
      if (!resp.ok) return false;
      delete list.shareSlug;
      this.#save(state);
      this.#dispatch({ action: 'unshare', listId });
      // Await the metafield write: the public read now 404s on any link once the
      // list no longer advertises its slug, so this is what actually revokes it.
      await this.#doSyncUp();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Public URL for a shared list, given its slug.
   * @param {string} slug
   * @returns {string}
   */
  static shareUrl(slug) {
    return `${location.origin}/pages/wishlist-shared?slug=${encodeURIComponent(slug)}`;
  }

  /**
   * @param {WishlistEventDetail} detail
   */
  static #dispatch(detail) {
    document.dispatchEvent(new WishlistUpdateEvent({ ...detail, totalCount: this.totalCount }));
  }

  /**
   * Reconcile the locally-cached lists against the current auth state. Clears
   * local data when the customer has signed out, or when a different customer
   * is now signed in on this browser, so one shopper never inherits another's
   * wishlist. Runs on every page load (guests included) since the sign-out
   * page renders with no customer.
   *
   * A guest→login transition does NOT clear: the guest's in-progress list is
   * preserved so #syncDown can merge it and push it up after they authenticate.
   */
  static reconcileOwner() {
    const current = Theme.customerId ? String(Theme.customerId) : null;
    let prev;
    try {
      prev = localStorage.getItem(this.#OWNER_KEY);
    } catch {
      prev = null;
    }
    // prev is only ever set while signed in, so a non-null prev that differs
    // from the current customer means sign-out or account-switch.
    if (prev && prev !== current) this.clearLocal();
    try {
      if (current) localStorage.setItem(this.#OWNER_KEY, current);
      else localStorage.removeItem(this.#OWNER_KEY);
    } catch {
      // private mode / quota — non-fatal
    }
  }

  /** Drop the locally-cached lists and reset any dependent UI (badge counts). */
  static clearLocal() {
    try {
      localStorage.removeItem(this.#LOCAL_KEY);
    } catch {
      // private mode / quota — non-fatal
    }
    this.#dispatch({ action: 'clear' });
  }

  static async init() {
    if (!Theme.customerId) return;
    await this.#syncDown();
  }

  static async #syncDown() {
    try {
      const resp = await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: `gid://shopify/Customer/${Theme.customerId}`,
          namespace: 'custom',
          key: 'wishlists',
          action: 'read',
        }),
      });
      if (!resp.ok) return;
      const body = await resp.json();
      const value = typeof body?.value === 'string' ? body.value : null;
      if (value) {
        const remote = /** @type {WishlistState} */ (JSON.parse(value));
        const merged = this.#merge(this.getLists(), remote);
        this.#save(merged);
        this.#dispatch({ action: 'sync' });
      }
      await this.#doSyncUp();
    } catch {
      // stay local silently
    }
  }

  static syncUp() {
    if (this.#syncTimer != null) {
      clearTimeout(this.#syncTimer);
    }
    this.#syncTimer = setTimeout(() => this.#doSyncUp(), 1000);
  }

  static async #doSyncUp() {
    if (!Theme.customerId) return;
    try {
      await fetch(PROXY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: `gid://shopify/Customer/${Theme.customerId}`,
          namespace: 'custom',
          key: 'wishlists',
          value: JSON.stringify(this.getLists()),
          action: 'write',
        }),
      });
    } catch {
      // fail silently
    }
  }

  /**
   * @param {WishlistState} local
   * @param {WishlistState} remote
   * @returns {WishlistState}
   */
  static #merge(local, remote) {
    const merged = { lists: [...local.lists] };
    for (const remoteList of remote.lists) {
      const localList = merged.lists.find((l) => l.id === remoteList.id);
      if (!localList) {
        merged.lists.push(remoteList);
      } else {
        // Union items — local order preserved, remote-only items appended
        const localIds = new Set(localList.items.map((i) => i.id));
        for (const item of remoteList.items) {
          if (!localIds.has(item.id)) localList.items.push(item);
        }
        // Carry a share link minted on another device so this device's UI
        // reflects it (and a subsequent syncUp doesn't drop it).
        if (!localList.shareSlug && remoteList.shareSlug) {
          localList.shareSlug = remoteList.shareSlug;
        }
      }
    }
    return merged;
  }

  /**
   * @returns {WishlistState}
   */
  static #defaultState() {
    return { lists: [{ id: 'default', name: 'My Wishlist', isDefault: true, items: [] }] };
  }
}

export class WishlistUpdateEvent extends Event {
  static eventName = 'wishlist:update';

  /**
   * @param {WishlistEventDetail} detail
   */
  constructor(detail) {
    super(WishlistUpdateEvent.eventName, { bubbles: true });
    /** @type {WishlistEventDetail} */
    this.detail = detail;
  }
}
